import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Table, Card, Select, Typography, Spin, Alert, Space, Button, Row, Col } from 'antd';
import { MinusSquareOutlined, PlusSquareOutlined, ReloadOutlined, LeftOutlined, DownOutlined, UpOutlined } from '@ant-design/icons';
import { getFromCacheE, saveExpiryToCache } from '../../utils/cacheUtils';
import { useAILimit } from '../user/AIButtonLimitChecker';
import AIUpgradePrompt from '../../shared/components/AIUpgradePrompt';
import { API_CONFIG, apiClient } from '../../api/config';
import activityTracker from '../user/ActivityTracker';
import { logger } from '../../utils/logger';
import { Bar } from 'react-chartjs-2';
import { useCompany } from '../../shared/context/CompanyContext';
import { useLocation } from 'react-router-dom';
import usePageTracking from '../../utils/usePageTracking';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title as ChartTitle,
  Tooltip,
  Legend,
} from 'chart.js';

// 註冊 Chart.js 組件
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ChartTitle,
  Tooltip,
  Legend
);

const { Option } = Select;
const { Title } = Typography;

const Balance = ({ initialSymbol }) => {
  // GA4 页面追踪
  usePageTracking('資產負債表');
  
  const { companyInfo, updateCompanyInfo } = useCompany();
  const location = useLocation();
  const [balanceData, setBalanceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState('quarter');
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState('');
  const [isAnalysisExpanded, setIsAnalysisExpanded] = useState(true);
  const ws = useRef(null);
  const analysisBufferRef = useRef('');
  const { checkAILimit, showUpgradePrompt, setShowUpgradePrompt, limitInfo } = useAILimit();

  const CACHE_KEY_PREFIX = 'balance_data_';

  // 處理 URL 參數中的 symbol
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const urlSymbol = searchParams.get('symbol');
    
    // 優先使用 URL 參數，其次是 initialSymbol，最後是 companyInfo
    const symbolToUse = urlSymbol || initialSymbol || companyInfo?.symbol;
    
    if (symbolToUse && symbolToUse !== companyInfo?.symbol) {
      logger.log('Balance: 從 URL 參數或 initialSymbol 獲取到新的 symbol:', symbolToUse);
      
      // 更新 CompanyContext
      updateCompanyInfo({
        symbol: symbolToUse,
        company_name: '',
        sector: '',
        industry: '',
        image: ''
      });
      
      logger.log('Balance: 已更新 symbol 到 CompanyContext');
    }
  }, [location.search, initialSymbol, companyInfo?.symbol, updateCompanyInfo]);

  // 使用 useCallback 包装 fetchBalanceData 函数
  const fetchBalanceData = useCallback(async (symbolToUse, periodToUse, forceRefresh = false) => {
    if (!symbolToUse) {
              logger.log('沒有提供股票代碼，顯示空狀態');
      setLoading(false);
      return;
    }
    
    const cacheKey = `${CACHE_KEY_PREFIX}${symbolToUse}_${periodToUse}`;
    
    if (!forceRefresh) {
      const cachedData = getFromCacheE(cacheKey);
      if (cachedData) {
        logger.log(`從緩存載入資產負債表數據: ${cacheKey}`);
        setBalanceData(cachedData);
        setLoading(false);
        return;
      }
    }
    
    setLoading(true);
    setError(null);
    try {
      const endpoint = API_CONFIG.FINANCIAL.BALANCE(symbolToUse, periodToUse);
      const response = await apiClient.get(endpoint);
      logger.log('API響應成功，數據長度:', response.data.length);
      
      if (response.data.length === 0) {
        setError('API返回了空數據');
        return;
      }
      
      setBalanceData(response.data);
      saveExpiryToCache(cacheKey, response.data, 0.5);
      
    } catch (err) {
      logger.error('獲取資產負債表數據失敗，錯誤詳情:', err);
      setError(`無法載入資產負債表資料: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  // 当公司信息或期间变化时获取数据
  useEffect(() => {
    if (companyInfo.symbol) {
      fetchBalanceData(companyInfo.symbol, period);
    }
  }, [companyInfo.symbol, period, fetchBalanceData]);

  // Markdown 轉 HTML（參考 list_10q10k.js）
  const convertMarkdownToHtml = (markdownText) => {
    if (!markdownText) return '';
    let html = markdownText;

    // 表格
    const tableRegex = /(\|[^\n]+\|\n\|[\s\-:|]+\|\n(\|[^\n]+\|\n?)+)/g;
    html = html.replace(tableRegex, (match) => {
      const lines = match.trim().split('\n');
      if (lines.length < 3) return match;
      let tableHtml = '<table style="border-collapse: collapse; width: 100%; margin: 10px 0; border: 1px solid #ddd; font-size: 13px;">';
      lines.forEach((line, index) => {
        if (index === 1) return; // 跳過分隔行
        const cells = line.split('|').filter(cell => cell.trim() !== '');
        if (cells.length === 0) return;
        const tag = index === 0 ? 'th' : 'td';
        tableHtml += '<tr>';
        cells.forEach((cell, cellIndex) => {
          const content = cell.trim();
          let style = '';
          if (index === 0) {
            style = 'background-color: #f2f2f2; font-weight: bold; padding: 6px 4px; border: 1px solid #ddd; text-align: center; font-size: 12px;';
          } else {
            style = 'padding: 6px 4px; border: 1px solid #ddd; text-align: left; font-size: 12px;';
            if (cellIndex === 0) {
              style += ' text-align: left;';
            } else {
              style += ' text-align: center;';
            }
          }
          tableHtml += `<${tag} style="${style}">${content}</${tag}>`;
        });
        tableHtml += '</tr>';
      });
      tableHtml += '</table>';
      return tableHtml;
    });

    // 粗體、斜體
    html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/__(.*?)__/g, '<strong>$1</strong>');
    html = html.replace(/\*(.*?)\*/g, '<em>$1</em>');
    html = html.replace(/_(.*?)_/g, '<em>$1</em>');

    // 標題
    html = html.replace(/^#### (.*$)/gim, '<h4 style="margin: 12px 0 6px 0; color: #1890ff; font-size: 16px;">$1</h4>');
    html = html.replace(/^### (.*$)/gim, '<h3 style="margin: 16px 0 8px 0; color: #1890ff; font-size: 18px;">$1</h3>');
    html = html.replace(/^## (.*$)/gim, '<h2 style="margin: 20px 0 12px 0; color: #1890ff; font-size: 20px;">$1</h2>');
    html = html.replace(/^# (.*$)/gim, '<h1 style="margin: 24px 0 16px 0; color: #1890ff; font-size: 22px;">$1</h1>');

    // 列表
    html = html.replace(/^\* (.*$)/gim, '<li style="margin: 4px 0;">$1</li>');
    html = html.replace(/^- (.*$)/gim, '<li style="margin: 4px 0;">$1</li>');

    // 段落
    html = html.replace(/\n\n/g, '</p><p style="margin: 8px 0;">');
    html = html.replace(/^(?!<[h|t|u|o|li])(.*$)/gim, '<p style="margin: 8px 0;">$1</p>');
    html = html.replace(/<p><\/p>/g, '');
    html = html.replace(/<p style="margin: 8px 0;"><\/p>/g, '');

    return html;
  };

  const handleRefreshData = () => {
    if (companyInfo.symbol) {
      fetchBalanceData(companyInfo.symbol, period, true);
    }
  };

  // 嘗試載入AI分析緩存
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const symbolFromUrl = urlParams.get('symbol') || '';
    const symbol = symbolFromUrl || companyInfo.symbol;
    if (!symbol) return;
    try {
      const cached = getFromCacheE(`ai_balance_analysis_${symbol}`);
      if (cached && cached.content) {
        setAnalysisResult(cached.content);
      }
    } catch (e) {}
  }, []);

  const handlePeriodChange = (value) => {
    setPeriod(value);
  };

  const handleExpand = (expanded, record, e) => {
    e.stopPropagation();

    if (expanded) {
      setExpandedKeys([...expandedKeys, record.key]);
    } else {
      setExpandedKeys(expandedKeys.filter(k => k !== record.key));
    }
  };

  // 將數據轉換為每個財年或季度作為一列，並添加可展開功能
  const transformData = () => {
    if (!balanceData || balanceData.length === 0) return [];

    // 獲取所有列名（除了不需要顯示的列）
    const excludeFields = ['symbol', 'fiscalYear', 'period'];
    const allFields = Object.keys(balanceData[0]).filter(field => !excludeFields.includes(field));

    // 定義欄位的自定義順序和分組關係
    const customOrder = [
      'date',

      // 流動資產分組
      'totalCurrentAssets',           // 父項目
      'cashAndCashEquivalents',
      'shortTermInvestments',
      'growthShortTermInvestmentsPercent',
      'netReceivables',
      'growthNetReceivablesPercent',
      'inventory',
      'growthInventoryPercent',
      'prepaids',
      'otherCurrentAssets',

      // 非流動資產分組
      'totalNonCurrentAssets',        // 父項目
      'propertyPlantEquipmentNet',
      'growthPropertyPlantEquipmentNetPercent',
      'goodwillAndIntangibleAssets',
      'longTermInvestments',
      'growthLongTermInvestmentsPercent',
      'taxAssets',
      'otherNonCurrentAssets',

      // 資產總計 (growthTotalAssetsPercent 作為其子項)
      'totalAssets',

      // 流動負債分組
      'totalCurrentLiabilities',      // 父項目
      'accountPayables',
      'growthAccountPayablesPercent',
      'shortTermDebt',
      'growthShortTermDebtPercent',
      'taxPayables',
      'deferredRevenue',
      'otherCurrentLiabilities',

      // 非流動負債分組
      'totalNonCurrentLiabilities',   // 父項目
      'longTermDebt',
      'growthLongTermDebtPercent',
      'deferredRevenueNonCurrent',
      'deferredTaxLiabilitiesNonCurrent',
      'otherNonCurrentLiabilities',

      // 負債總計 (growthTotalLiabilitiesPercent 作為其子項)
      'totalLiabilities',

      // 股東權益分組
      'totalStockholdersEquity',      // 父項目
      'commonStock',
      'retainedEarnings',
      'growthRetainedEarningsPercent',
      'additionalPaidInCapital',
      'accumulatedOtherComprehensiveIncomeLoss',
      'otherTotalStockholdersEquity'
    ];

    // 定義父子項目關係
    const groupMap = {
      'totalCurrentAssets': [
        'cashAndCashEquivalents',
        'shortTermInvestments',
        'growthShortTermInvestmentsPercent',
        'netReceivables',
        'growthNetReceivablesPercent',
        'inventory',
        'growthInventoryPercent',
        'prepaids',
        'otherCurrentAssets'
      ],
      'totalNonCurrentAssets': [
        'propertyPlantEquipmentNet',
        'growthPropertyPlantEquipmentNetPercent',
        'goodwillAndIntangibleAssets',
        'longTermInvestments',
        'growthLongTermInvestmentsPercent',
        'taxAssets',
        'otherNonCurrentAssets'
      ],
      'totalAssets': [
        'growthTotalAssetsPercent'
      ],
      'totalCurrentLiabilities': [
        'accountPayables',
        'growthAccountPayablesPercent',
        'shortTermDebt',
        'growthShortTermDebtPercent',
        'taxPayables',
        'deferredRevenue',
        'otherCurrentLiabilities'
      ],
      'totalNonCurrentLiabilities': [
        'longTermDebt',
        'growthLongTermDebtPercent',
        'deferredRevenueNonCurrent',
        'deferredTaxLiabilitiesNonCurrent',
        'otherNonCurrentLiabilities'
      ],
      'totalLiabilities': [
        'growthTotalLiabilitiesPercent'
      ],
      'totalStockholdersEquity': [
        'growthTotalStockholdersEquityPercent',
        'commonStock',
        'retainedEarnings',
        'growthRetainedEarningsPercent',
        'additionalPaidInCapital',
        'accumulatedOtherComprehensiveIncomeLoss',
        'otherTotalStockholdersEquity'
      ]
    };

    // 收集所有子項目
    const allChildren = Object.values(groupMap).flat();

    // 按自定義順序排序欄位
    const sortedFields = [...allFields].sort((a, b) => {
      const indexA = customOrder.indexOf(a);
      const indexB = customOrder.indexOf(b);

      // 如果欄位不在自定義順序中，放到最後
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;

      return indexA - indexB;
    });

    // 創建一個映射，存儲每個欄位的數據行
    const rowsMap = {};

    // 創建一個結構來保存各個財年/季度的數據
    const columnKeys = balanceData.map(data => {
      return {
        data,
        key: period === 'quarter' 
          ? `${data.fiscalYear}${data.period}`
          : data.fiscalYear
      };
    });

    // 輔助函數：填充行數據
    const fillRowData = (rowData, field) => {
      columnKeys.forEach(({data, key}) => {
        if (data[field] !== undefined) {
          rowData[key] = data[field];
        } else if (['totalAssets', 'totalLiabilities'].includes(field)) {
          // 為重要的合計項目設置特殊樣式或處理
          rowData[key] = data[field] || 'N/A';
        } else {
          rowData[key] = 'N/A';
        }
      });
      return rowData;
    };

    // 先創建所有父項目的行
    Object.keys(groupMap).forEach(parentField => {
      if (sortedFields.includes(parentField)) {
        const rowData = { 
          field: parentField,
          key: parentField,
          children: [] 
        };
        // 填充數據
        fillRowData(rowData, parentField)
        rowsMap[parentField] = rowData;
      }
    });

    // 添加其他非父項目的行
    sortedFields.forEach(field => {
      // 跳過已經添加的父項目和子項目
      if (rowsMap[field] || allChildren.includes(field)) return;

      const rowData = { field, key: field };

      // 填充數據
      fillRowData(rowData, field);
      rowsMap[field] = rowData;
    });

    // 為每個父項目添加其子項目
    Object.entries(groupMap).forEach(([parent, children]) => {
      if (!rowsMap[parent]) return;

      children.forEach(childField => {
        // 確保字段存在於數據中
        if (!sortedFields.includes(childField) ) return;

        const childRowData = { 
          field: childField,
          key: childField
        };

        // 填充子項目數據
        fillRowData(childRowData, childField);
        rowsMap[parent].children.push(childRowData);
      });
    });

    // 確保分隔行顯示在正確的位置
    const finalData = sortedFields
      .filter(field => !allChildren.includes(field))
      .map(field => rowsMap[field]);
    
    return finalData;
  };
  // 定義需要顯示為深藍色的欄位
  const blueTextFields = [ 
    'totalAssets', 
    'totalLiabilities',
    'totalStockholdersEquity'  // 保持股東權益總計顯示深藍色
  ];

  // 定義需要右對齊的欄位
  const rightAlignFields = [
    'totalAssets', 'growthTotalAssetsPercent',
    'totalLiabilities', 'growthTotalLiabilitiesPercent'
  ];

  // 生成表格列配置
  const generateColumns = () => {
    if (!balanceData || balanceData.length === 0) return [];

    // 創建一個集合來存儲唯一的列標識
    const columnKeys = new Set();

    const columns = [
      {
        title: '財務年(季)度',
        dataIndex: 'field',
        key: 'field',
        width: 120,
        fixed: 'left',
        render: (value, record) => {
          // 美化字段名顯示
          const fieldLabels = {
            'date': '結算日期',

            // 流動資產
            'totalCurrentAssets': '流動資產總計',
            'cashAndCashEquivalents': '現金及現金等價物',
            'shortTermInvestments': '短期投資',
            'growthShortTermInvestmentsPercent': '短期投資YoY',
            'netReceivables': '應收帳款淨額',
            'growthNetReceivablesPercent': '應收帳款YoY',
            'inventory': '存貨',
            'growthInventoryPercent': '存貨YoY',
            'prepaids': '預付費用',
            'otherCurrentAssets': '其他流動資產',

            // 非流動資產
            'totalNonCurrentAssets': '非流動資產總計',
            'propertyPlantEquipmentNet': '固定資產淨額',
            'growthPropertyPlantEquipmentNetPercent': '固定資產YoY',
            'goodwillAndIntangibleAssets': '商譽及無形資產',
            'longTermInvestments': '長期投資',
            'growthLongTermInvestmentsPercent': '長期投資YoY',
            'taxAssets': '稅務資產',
            'otherNonCurrentAssets': '其他非流動資產',

            'totalAssets': '資產總計',
            'growthTotalAssetsPercent': '資產總計YoY',

            // 流動負債
            'totalCurrentLiabilities': '流動負債總計',
            'accountPayables': '應付帳款',
            'growthAccountPayablesPercent': '應付帳款YoY',
            'shortTermDebt': '短期債務',
            'growthShortTermDebtPercent': '短期債務YoY',
            'taxPayables': '應付稅款',
            'deferredRevenue': '遞延收入',
            'otherCurrentLiabilities': '其他流動負債',

            // 非流動負債
            'totalNonCurrentLiabilities': '非流動負債總計',
            'longTermDebt': '長期債務',
            'growthLongTermDebtPercent': '長期債務YoY',
            'deferredRevenueNonCurrent': '非流動遞延收入',
            'deferredTaxLiabilitiesNonCurrent': '非流動遞延稅務負債',
            'otherNonCurrentLiabilities': '其他非流動負債',
            
            'totalLiabilities': '負債總計',
            'growthTotalLiabilitiesPercent': '負債總計YoY',

            // 股東權益
            'totalStockholdersEquity': '股東權益總計',
            'commonStock': '普通股',
            'retainedEarnings': '保留盈餘',
            'growthRetainedEarningsPercent': '保留盈餘YoY',
            'additionalPaidInCapital': '資本公積',
            'accumulatedOtherComprehensiveIncomeLoss': '其他綜合損益',
            'otherTotalStockholdersEquity': '其他股東權益',
            'growthTotalStockholdersEquityPercent': '股東權益總計YoY'
          };

          const displayName = fieldLabels[value] || value;

          // 定義不同類型欄位的樣式
          let textStyle = {};
          let className = '';
          // 右對齊項目
          if (rightAlignFields.includes(value)) {
            className = 'right-aligned-text';
            if (blueTextFields.includes(value)) {
              textStyle = { color: '#1a2952', fontWeight: 'bold' };
            }
          }
          // 一般的合計項目使用深藍色粗體
          else if (blueTextFields.includes(value)) {
            textStyle = { color: '#1a2952', fontWeight: 'bold' };
          }

          // 如果有子項目，添加展開/縮放圖標
          if (record.children && record.children.length > 0) {
            return (
              <span className="expandable-row-label">
                <span className={className} style={textStyle}>{displayName}</span>
                <span className="expand-icon" onClick={(e) => {
                  const expanded = expandedKeys.includes(record.key);
                  // 觸發展開/收起
                  handleExpand(!expanded, record, e);
                  e.stopPropagation();
                }}>
                  {expandedKeys.includes(record.key) ? 
                    <MinusSquareOutlined style={{ marginLeft: 8 }} /> : 
                    <PlusSquareOutlined style={{ marginLeft: 8 }} />
                  }
                </span>
              </span>
            );
          }
          
          return <span className={className} style={textStyle}>{displayName}</span>;
        }
      }
    ];

    // 為每個財年/季度的數據添加列定義
    balanceData.forEach(data => {
      // 根據 period 參數決定如何創建列標識
      const columnKey = period === 'quarter' 
        ? `${data.fiscalYear}${data.period}` // 季度數據
        : data.fiscalYear;                    // 年度數據

      // 避免重複列
      if (!columnKeys.has(columnKey)) {
        columnKeys.add(columnKey);

        columns.push({
          title: columnKey,
          dataIndex: columnKey,
          key: columnKey,
          align: 'right', // 所有數據列默認右對齊
          width: 100,
          render: (text, record) => {
            // 對於成長率欄位，根據正負值設置顏色
            if (record.field.includes('growth') && record.field.includes('Percent')) {
              // 檢查是否包含負號（如 "-2.5 %"）
              const isNegative = text && text.includes('-');

              return (
                <span style={{ 
                  color: isNegative ? '#ff4d4f' : '#52c41a',  // 負數紅色，正數綠色
                  fontWeight: 'bold'
                }}>
                  {text}
                </span>
              );
            }
            // 對於合計欄位，設置深藍色
            if (blueTextFields.includes(record.field)) {
              return (
                <span style={{ 
                  color: '#1a2952',  // 深藍色 
                  fontWeight: 'bold'
                }}>
                  {text}
                </span>
              );
            }

            // 其他欄位維持原樣
            return text;
          }
        });
      }
    });

    // 確保列按時間順序排序（最新的在左側）
    columns.sort((a, b) => {
      if (a.dataIndex === 'field') return -1; // 項目列始終在最左側
      if (b.dataIndex === 'field') return 1;

      // 直接比較字符串表示
      return String(b.dataIndex) > String(a.dataIndex) ? 1 : -1;
    });
    return columns;
  };

  const handleAnalyze = async () => {
    if (!companyInfo.symbol) return;

    // AI 使用次數檢查
    const limitCheck = await checkAILimit('AI-balance');
    if (!limitCheck.canProceed) {
      setAnalysisResult(limitCheck.reason || 'AI 使用次數已達上限');
      return;
    }

    setIsAnalyzing(true);
    setAnalysisResult('');
    analysisBufferRef.current = '';
    
    const wsProtocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const wsHost = window.location.hostname === 'localhost'
      ? 'localhost:5080'
      : window.location.host;
    const wsUrl = `${wsProtocol}://${wsHost}/ws-app`;

    if (!ws.current || ws.current.readyState !== WebSocket.OPEN) {
      ws.current = new WebSocket(wsUrl);
      
      ws.current.onopen = () => {
        ws.current.send(JSON.stringify({
          type: 'balance-analysis',
          symbol: companyInfo.symbol
        }));
      };
      
      ws.current.onmessage = (event) => {
        const data = JSON.parse(event.data);
        if (data.type === 'balance') {
          analysisBufferRef.current += data.content || '';
          const html = convertMarkdownToHtml(analysisBufferRef.current);
          setAnalysisResult(html);
        } else if (data.type === 'complete') {
          try {
            const cacheKey = `ai_balance_analysis_${companyInfo.symbol}`;
            const finalHtml = convertMarkdownToHtml(analysisBufferRef.current);
            saveExpiryToCache(cacheKey, {
              symbol: companyInfo.symbol,
              content: finalHtml,
              updatedAt: Date.now()
            }, 90);
          } catch (e) {}
          setIsAnalyzing(false);
        } else if (data.error) {
          setAnalysisResult(data.error);
          setIsAnalyzing(false);
        }
      };
      
      ws.current.onerror = (error) => {
        console.error('WebSocket error:', error);
        setAnalysisResult('分析過程中發生錯誤');
        setIsAnalyzing(false);
      };
    } else {
      ws.current.send(JSON.stringify({
        type: 'balance-analysis',
        symbol: companyInfo.symbol
      }));
    }

    activityTracker.trackBtnExecution('AI-balance', companyInfo.symbol, window.location.pathname);
  };

  useEffect(() => {
    
    const urlParams = new URLSearchParams(window.location.search);
    const symbol = urlParams.get('symbol') || '';
    if (symbol) {
      activityTracker.trackPageView(symbol);
    }
    // 嘗試載入AI分析緩存
    const analysisKey = symbol ? `ai_balance_analysis_${symbol}` : (companyInfo.symbol ? `ai_balance_analysis_${companyInfo.symbol}` : null);
    if (analysisKey) {
      try {
        const cached = getFromCacheE(analysisKey);
        if (cached && cached.content) {
          setAnalysisResult(cached.content);
        }
      } catch (e) {}
    }
  }, []);

  // 处理带单位的数据
  const parseValueWithUnit = (value) => {
    if (value == null) return 0;
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      const normalized = value.replace(/,/g, '').trim();
      // 支援 K/M/B 單位，統一換算為 M（百萬）
      const match = normalized.match(/^(-?\d+(\.\d+)?)(\s*[KMB])?$/i);
      if (match) {
        const num = parseFloat(match[1]);
        const unit = (match[3] || '').trim().toUpperCase();
        if (unit === 'K') return num / 1000;   // 千 -> 百萬
        if (unit === 'M') return num;          // 百萬 -> 百萬
        if (unit === 'B') return num * 1000;   // 十億 -> 百萬
        return num; // 無單位，視為已是 M
      }
      // 後備：移除所有字母再嘗試解析
      const fallback = parseFloat(normalized.replace(/[A-Za-z]/g, ''));
      return isNaN(fallback) ? 0 : fallback;
    }
    return 0;
  };

  // 准备图表数据
  const prepareChartData = () => {
    if (!balanceData || balanceData.length === 0) return null;

    // 按时间倒序排序数据（最新的在前）
    const sortedData = [...balanceData].sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateB - dateA;
    });

    // 只取最近20条数据
    const recentData = sortedData.slice(0, 20);

    // 准备标签（日期）
    const labels = recentData.map(item => {
      const date = new Date(item.date);
      return period === 'quarter' 
        ? `${date.getFullYear()}${item.period}`
        : `${date.getFullYear()}`;
    });

    // 准备第一个 Bar Chart 数据（存货、应收账款、应付账款）
    const barData1 = {
      labels,
      datasets: [
        {
          label: '存貨',
          data: recentData.map(item => parseValueWithUnit(item.inventory)),
          backgroundColor: 'rgba(54, 162, 235, 0.5)',
          borderColor: 'rgba(54, 162, 235, 1)',
          borderWidth: 1,
        },
        {
          label: '應收賬款淨額',
          data: recentData.map(item => parseValueWithUnit(item.netReceivables)),
          backgroundColor: 'rgba(255, 99, 132, 0.5)',
          borderColor: 'rgba(255, 99, 132, 1)',
          borderWidth: 1,
        },
        {
          label: '應付賬款',
          data: recentData.map(item => parseValueWithUnit(item.accountPayables)),
          backgroundColor: 'rgba(75, 192, 192, 0.5)',
          borderColor: 'rgba(75, 192, 192, 1)',
          borderWidth: 1,
        }
      ],
    };

    // 准备第二个 Bar Chart 数据（流动资产、流动负债）
    const barData2 = {
      labels,
      datasets: [
        {
          label: '流動資產總計',
          data: recentData.map(item => parseValueWithUnit(item.totalCurrentAssets)),
          backgroundColor: 'rgba(153, 102, 255, 0.5)',
          borderColor: 'rgba(153, 102, 255, 1)',
          borderWidth: 1,
        },
        {
          label: '流動負債總計',
          data: recentData.map(item => parseValueWithUnit(item.totalCurrentLiabilities)),
          backgroundColor: 'rgba(255, 159, 64, 0.5)',
          borderColor: 'rgba(255, 159, 64, 1)',
          borderWidth: 1,
        }
      ],
    };

    return { barData1, barData2 };
  };

  // 图表配置
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += new Intl.NumberFormat('zh-TW', {
                style: 'decimal',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              }).format(context.parsed.y) + ' M';
            }
            return label;
          }
        }
      }
    },
    scales: {
      x: {
        ticks: {
          maxRotation: 45,
          minRotation: 45
        },
        grid: {
          display: false
        }
      },
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value) {
            return new Intl.NumberFormat('zh-TW', {
              style: 'decimal',
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            }).format(value) + ' M';
          }
        }
      }
    },
    barPercentage: 0.8,
    categoryPercentage: 0.9
  };

  return (
    <div className="container mt-4">
      <Card
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <div className="card-title">
              <Button 
                type="primary"
                onClick={handleAnalyze}
                loading={isAnalyzing}
                icon={<ReloadOutlined />}
                style={{ backgroundColor: '#1890ff', borderColor: '#1890ff', fontWeight: 'bold' }}
              >
                {isAnalyzing ? '分析中...' : '分析最新季度'}
              </Button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Select 
                defaultValue="quarter"
                style={{ width: 120 }} 
                onChange={handlePeriodChange}
                loading={loading}
              >
                <Option value="FY">年度</Option>
                <Option value="quarter">季度</Option>
              </Select>
              <Button onClick={handleRefreshData} style={{ marginLeft: 0 }}>刷新數據</Button>
            </div>
          </div>
        } 
        className="financial-card"
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {analysisResult && (
            <Card className="mb-4">
              <div 
                style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  cursor: 'pointer'
                }}
                onClick={() => setIsAnalysisExpanded(!isAnalysisExpanded)}
              >
                <Title level={4} style={{ margin: 0 }}>分析結果</Title>
                {isAnalysisExpanded ? <UpOutlined /> : <DownOutlined />}
              </div>
              {isAnalysisExpanded && (
                <div 
                  className="ai-analysis-html"
                  style={{ lineHeight: '1.6', fontSize: '14px', marginTop: '16px' }}
                  dangerouslySetInnerHTML={{ __html: analysisResult }}
                />
              )}
            </Card>
          )}
          {error && <Alert type="error" message={error} />}

          {loading ? (
            <div style={{ textAlign: 'center', margin: '50px 0' }}>
              <Spin size="large" />
            </div>
          ) : (
            <>
              {/* 添加图表 */}
              {balanceData && balanceData.length > 0 && (
                <div style={{ marginTop: '10px', marginBottom: '20px' }}>
                  <Row gutter={[16, 16]}>
                    <Col xs={24} lg={12}>
                      <Card title="存貨、應收賬款與應付賬款趨勢" className="mb-4">
                        <div style={{ height: '300px', overflowX: 'auto' }}>
                          <div style={{ minWidth: '600px', height: '100%' }}>
                            <Bar data={prepareChartData()?.barData1} options={chartOptions} />
                          </div>
                        </div>
                        {/* 添加时间提示 */}
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          marginTop: '5px', 
                          padding: '0 15px',
                          fontSize: '14px',
                          color: '#333'
                        }}>
                          <span style={{ fontWeight: 'bold' }}>近期</span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                            <LeftOutlined style={{ fontSize: '18px', color: '#1890ff', fontWeight: 'bold' }} />
                            <LeftOutlined style={{ fontSize: '18px', color: '#1890ff', fontWeight: 'bold' }} />
                            <LeftOutlined style={{ fontSize: '18px', color: '#1890ff', fontWeight: 'bold' }} />
                          </div>
                          <span style={{ fontWeight: 'bold' }}>遠期</span>
                        </div>
                      </Card>
                    </Col>
                    <Col xs={24} lg={12}>
                      <Card title="流動資產與流動負債趨勢">
                        <div style={{ height: '300px', overflowX: 'auto' }}>
                          <div style={{ minWidth: '600px', height: '100%' }}>
                            <Bar data={prepareChartData()?.barData2} options={chartOptions} />
                          </div>
                        </div>
                        {/* 添加时间提示 */}
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          marginTop: '5px', 
                          padding: '0 15px',
                          fontSize: '14px',
                          color: '#333'
                        }}>
                          <span style={{ fontWeight: 'bold' }}>近期</span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                            <LeftOutlined style={{ fontSize: '18px', color: '#1890ff', fontWeight: 'bold' }} />
                            <LeftOutlined style={{ fontSize: '18px', color: '#1890ff', fontWeight: 'bold' }} />
                            <LeftOutlined style={{ fontSize: '18px', color: '#1890ff', fontWeight: 'bold' }} />
                          </div>
                          <span style={{ fontWeight: 'bold' }}>遠期</span>
                        </div>
                      </Card>
                    </Col>
                  </Row>
                </div>
              )}

              <Table 
                columns={generateColumns()} 
                dataSource={transformData()} 
                rowKey="field"
                pagination={false}
                scroll={{ x: 'max-content' }} 
                size="middle"
                tableLayout="fixed"
                bordered
                expandedRowKeys={expandedKeys}
                onExpand={(expanded, record) => {
                  handleExpand(expanded, record, { stopPropagation: () => {} });
                }}
                expandable={{
                  expandIcon: () => null, // 隱藏默認的展開圖標
                  expandRowByClick: true
                }}
              />
            </>
          )}
        </Space>

        <AIUpgradePrompt
          visible={showUpgradePrompt}
          onCancel={() => setShowUpgradePrompt(false)}
          limitInfo={limitInfo}
        />

        <style jsx>{`
          .financial-card {
            margin-bottom: 20px;
          }
          .expandable-row-label {
            display: flex;
            align-items: center;
            justify-content: space-between;
          }
          .expand-icon {
            cursor: pointer;
          }
          .ant-table {
            font-size: 14px;
          }
          .ant-table-thead > tr > th {
            font-size: 14px;
            padding: 8px;
          }
          .ant-table-tbody > tr > td {
            font-size: 14px;
            padding: 8px;
          }
          .ant-card-head-title {
            font-size: 16px;
          }
          .ant-select {
            font-size: 14px;
          }
          .ant-select-selector {
            font-size: 14px !important;
          }
          .ant-btn {
            font-size: 14px;
          }
          .ant-btn[style*="background-color: #ffffff"]:hover {
            background: #f5f5f5 !important;
            border-color: #bfbfbf !important;
            color: #000000 !important;
          }

          @media screen and (max-width: 768px) {
            .ant-table {
              font-size: 11px;
            }
            .ant-table-thead > tr > th {
              font-size: 11px;
              padding: 4px;
            }
            .ant-table-tbody > tr > td {
              font-size: 11px;
              padding: 4px;
            }
            .ant-card-head-title {
              font-size: 14px;
            }
            .ant-select {
              font-size: 11px;
            }
            .ant-select-selector {
              font-size: 11px !important;
            }
            .ant-btn {
              font-size: 11px;
            }
          }

          @media screen and (max-width: 480px) {
            .ant-table {
              font-size: 12px;
            }
            .ant-table-thead > tr > th {
              font-size: 12px;
              padding: 1px;
            }
            .ant-table-tbody > tr > td {
              font-size: 12px;
              padding: 1px;
            }
            .ant-card-head-title {
              font-size: 14px;
            }
            .ant-select {
              font-size: 12px;
            }
            .ant-select-selector {
              font-size: 12px !important;
            }
            .ant-btn {
              font-size: 12px;
            }
          }
        `}</style>
      </Card>
    </div>
  );
};

export default Balance; 